import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import {
  Button,
  Card,
  Numeric,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { formatGBP } from "../lib/format.js";
import { useToast } from "../lib/toast.js";
import type { InvoiceDTO } from "@wmart/shared-types";

// UUID validator for the `?ids=` query param (P15-T10 fixup). Filters out
// non-UUID tokens before fetch so a malformed/garbage URL surfaces as an
// inline error instead of cascading into a 400/404 storm from the API.
const UuidSchema = z.string().uuid();

/**
 * MergeInvoicesWizard — review + confirm screen for
 * POST /v1/admin/invoices/merge (P15-T8).
 *
 * The InvoicesList page navigates here with `?ids=uuid1,uuid2,...` after the
 * operator multi-selects ≥2 draft invoices. We hydrate the full
 * InvoiceDTO for each id, show a summary table with totals, and POST on
 * confirm. Server-side guards (same-customer, same-company, all-draft) are
 * authoritative — any mismatch surfaces as a 422 toast with detail.
 *
 * On success: navigate to the newly-issued merged invoice's detail page.
 */
export function MergeInvoicesWizard() {
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState<InvoiceDTO[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);

  // Parse + validate `?ids=` once per searchParams change. `tokens` is the
  // raw split; `ids` is only the well-formed UUIDs; `hasInvalid` is true
  // if any token failed validation so we can render an inline error
  // (avoids POSTing garbage to /v1/admin/invoices/:id).
  const { ids, hasInvalid } = useMemo(() => {
    const raw = searchParams.get("ids") ?? "";
    const tokens = raw
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const valid: string[] = [];
    let invalid = false;
    for (const t of tokens) {
      if (UuidSchema.safeParse(t).success) valid.push(t);
      else invalid = true;
    }
    return { ids: valid, hasInvalid: invalid };
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      if (ids.length < 2) {
        setLoadError(true);
        return;
      }
      try {
        const results = await Promise.all(
          ids.map((id) => api.get<InvoiceDTO>(`/v1/admin/invoices/${id}`)),
        );
        if (cancelled) return;
        setInvoices(results);
        setLoadError(false);
      } catch (err) {
        if (cancelled) return;
        setLoadError(true);
        if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
        else notify("error", "Failed to load invoices for merge");
      }
    }
    void fetchAll();
    return () => {
      cancelled = true;
    };
  }, [ids, notify, notifyProblem]);

  // Surface client-side preview of the merge guards. The server is
  // authoritative; this just avoids a wasted POST when the operator's
  // selection is obviously inconsistent.
  const allDrafts = invoices.length > 0 && invoices.every((i) => i.status === "draft");
  const sameCustomer =
    invoices.length > 0 &&
    invoices.every((i) => i.customerId === invoices[0]!.customerId);
  const sameCompany =
    invoices.length > 0 &&
    invoices.every((i) => i.companyId === invoices[0]!.companyId);
  const canMerge = ids.length >= 2 && allDrafts && sameCustomer && sameCompany;

  const totalMinor = invoices.reduce((s, i) => s + i.totalMinor, 0);
  const subtotalMinor = invoices.reduce((s, i) => s + i.subtotalMinor, 0);
  const vatMinor = invoices.reduce((s, i) => s + i.vatMinor, 0);

  async function onConfirm() {
    if (!canMerge) return;
    setBusy(true);
    try {
      const res = await api.post<{ invoiceId: string; number: string }>(
        "/v1/admin/invoices/merge",
        { invoiceIds: ids },
      );
      notify("success", `Merged into invoice ${res.number}`);
      navigate(`/invoices/${res.invoiceId}`);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Merge failed");
    } finally {
      setBusy(false);
    }
  }

  if (hasInvalid) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/invoices" className="text-body-sm text-accent hover:underline">
          ← Back to invoices
        </Link>
        <p className="text-body-sm text-danger" role="alert">
          Invalid invoice id provided. Re-select the drafts from the
          invoices list and try again.
        </p>
      </div>
    );
  }

  if (ids.length < 2) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/invoices" className="text-body-sm text-accent hover:underline">
          ← Back to invoices
        </Link>
        <p className="text-body-sm text-danger">
          Select 2 or more draft invoices from the list to merge.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/invoices" className="text-body-sm text-accent hover:underline">
          ← Back to invoices
        </Link>
        <p className="text-body-sm text-danger">
          One or more invoices could not be loaded.
        </p>
      </div>
    );
  }

  if (invoices.length === 0) {
    return <p className="text-body-sm text-ink-500">Loading…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <Link to="/invoices" className="text-body-sm text-accent hover:underline">
        ← Back to invoices
      </Link>

      <Card className="p-6">
        <h2 className="font-serif text-display-sm text-ink-900">
          Merge {invoices.length} draft invoices
        </h2>
        <p className="mt-2 text-body-sm text-ink-700">
          A single new invoice will be issued with a fresh gap-free number;
          the source drafts will be voided. This action cannot be undone via
          the admin UI (re-issue individual orders if a rollback is needed).
        </p>
      </Card>

      <Card className="p-6">
        <h3 className="font-serif text-title-md text-ink-900">Sources</h3>
        <Table>
          <THead>
            <TR>
              <TH>Number</TH>
              <TH>Status</TH>
              <TH>Customer</TH>
              <TH>Company</TH>
              <TH className="text-right">Total</TH>
            </TR>
          </THead>
          <TBody>
            {invoices.map((inv) => (
              <TR key={inv.id}>
                <TD className="font-mono">{inv.number}</TD>
                <TD>
                  <StatusPill tone={inv.status === "draft" ? "neutral" : "danger"}>
                    {inv.status}
                  </StatusPill>
                </TD>
                <TD className="font-mono text-body-sm">
                  {inv.customerId.slice(0, 8)}…
                </TD>
                <TD className="font-mono text-body-sm">
                  {inv.companyId.slice(0, 8)}…
                </TD>
                <TD className="text-right">
                  <Numeric serif>{formatGBP(inv.totalMinor)}</Numeric>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>

      <Card className="p-6">
        <h3 className="font-serif text-title-md text-ink-900">Merged totals</h3>
        <dl className="mt-3 flex flex-col gap-1 text-body-sm">
          <div className="flex justify-between">
            <dt>Subtotal</dt>
            <dd>
              <Numeric serif>{formatGBP(subtotalMinor)}</Numeric>
            </dd>
          </div>
          <div className="flex justify-between">
            <dt>VAT</dt>
            <dd>
              <Numeric>{formatGBP(vatMinor)}</Numeric>
            </dd>
          </div>
          <div className="flex justify-between font-medium text-ink-900">
            <dt>Total</dt>
            <dd>
              <Numeric serif>{formatGBP(totalMinor)}</Numeric>
            </dd>
          </div>
        </dl>
      </Card>

      {!allDrafts ? (
        <p className="text-body-sm text-danger">
          All selected invoices must be in draft status to merge.
        </p>
      ) : null}
      {!sameCustomer ? (
        <p className="text-body-sm text-danger">
          All selected invoices must belong to the same customer.
        </p>
      ) : null}
      {!sameCompany ? (
        <p className="text-body-sm text-danger">
          All selected invoices must belong to the same company.
        </p>
      ) : null}

      <div className="flex gap-3">
        <Button
          variant="ghost"
          onClick={() => navigate("/invoices")}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          onClick={() => void onConfirm()}
          disabled={!canMerge || busy}
          loading={busy}
        >
          Confirm merge
        </Button>
      </div>
    </div>
  );
}
